<template>
  <section class="page">
    <div class="page__header">
      <h2 class="page__title">{{ $t('kps.officersWork.title') }}</h2>
    </div>

    <div class="section-card">
      <DataTable :columns="visibleColumns" :rows="rows" :rowKey="rowKey" :row-height="56">
        <template #cell-officer="{ row }">
          <select class="tbl-select" v-model="row.officerKey" @change="applyOfficer(row)">
            <option value="">{{ $t('kps.officersWork.pickOfficer') }}</option>
            <option v-for="opt in officerOptions" :key="opt.renderKey || opt.key" :value="opt.key">{{ opt.label }}</option>
          </select>
        </template>
        <template #cell-position="{ row }">
          <input class="tbl-input" v-model="row.position" />
        </template>
        <template #cell-rank="{ row }">
          <input class="tbl-input" v-model="row.rank" />
        </template>
        <template #cell-full_name="{ row }">
          <input class="tbl-input" v-model="row.full_name" />
        </template>
        <template #cell-goal="{ row }">
          <input class="tbl-input" v-model="row.goal" />
        </template>
        <template #cell-arrival="{ row }">
          <input class="tbl-input" type="datetime-local" v-model="row.arrival" />
        </template>
        <template #cell-departure="{ row }">
          <input class="tbl-input" type="datetime-local" v-model="row.departure" />
        </template>
        <template #cell-sign="{ row }">
          <input class="tbl-input" v-model="row.sign" />
        </template>
        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">

            <button class="actions__btn actions__btn--delete" @click="removeRow(row)" :title="$t('common.delete')" :aria-label="$t('common.delete')">
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>
      <div style="margin-top: 8px; display: flex; justify-content: flex-end; gap: 8px; padding: 0 12px 12px;">
        <button class="btn-small" :disabled="saving" @click="save">{{ $t('common.save') }}</button>
        <ActionButton v-if="!readOnly" :color="'#2A8A1B'" :text="$t('kps.officersWork.addRecord')" @click="addRow" />
      </div>
    </div>
    <div class="page__footer">
      <ViewNavButtons
          :prev-text="$t('common.back')"
          :next-text="$t('common.next')"
          :prev-to="{ path: '/kps/calendar' }"
          :next-to="{ path: '/kps/inspections' }"
      />
    </div>

    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">{{ toastMessage }}</div>
      </div>
    </transition>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import { listCommanders, listOfficersWork, saveOfficersWork } from '@/api/officersWork'

import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { canEdit } from '@/access/armAccess'

export default {
  name: 'KpsOfficersWorkView',
  components: { DataTable, ViewNavButtons , ActionButton },
  data() {
    return {
      currentUser: null,
      rows: [],
      officerOptions: [],
      loading: false,
      saving: false,
      error: null,
      toastMessage: '',
      showToast: false,
    }
  },
  computed: {
    canEditKps() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/kps/section-4')
    },
    readOnly() { return !this.canEditKps },
    columns() {
      return [
        { key: 'officer', label: this.$t('kps.officersWork.colOfficerPick'), width: 220 },
        { key: 'position', label: this.$t('kps.officersWork.colPosition'), width: 180 },
        { key: 'rank', label: this.$t('kps.officersWork.colRank'), width: 160 },
        { key: 'full_name', label: this.$t('kps.officersWork.colFullName'), width: 240 },
        { key: 'goal', label: this.$t('kps.officersWork.colGoal'), width: 220 },
        { key: 'arrival', label: this.$t('kps.officersWork.colArrival'), width: 220 },
        { key: 'departure', label: this.$t('kps.officersWork.colDeparture'), width: 220 },
        { key: 'sign', label: this.$t('kps.officersWork.colSign'), width: 140 },
        { key: 'actions', label: '', width: 100 },
      ]
    },
    visibleColumns() {
      return this.readOnly ? this.columns.filter(c => c.key !== 'actions') : this.columns;
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    this.fetchRows()
  },
  methods: {
    officerKeyFromFields({ full_name, position, rank }) {
      const norm = (v) => String(v || '').trim().replace(/\s+/g, ' ')
      // deterministic key so selection can be restored after reload
      return `cmd:${norm(full_name)}|${norm(position)}|${norm(rank)}`
    },
    rowKey(r) {
      return r.__id ?? r.id ?? `${r.position}-${r.full_name}`
    },
    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 2500)
    },
    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 3500)
    },
    async fetchRows() {
      try {
        this.loading = true
        const commanders = await listCommanders().catch((e) => {
          console.error('OfficersWork: commanders load failed', e)
          return []
        })
        const saved = await listOfficersWork().catch((e) => {
          console.error('OfficersWork: saved rows load failed', e)
          // Не блокируем экран (офицеров всё равно можно выбрать и заполнить форму)
          return []
        })

        const cmdItems = Array.isArray(commanders) ? commanders : []
        this.officerOptions = cmdItems.map((it, idx) => {
          const full = it.full_name ?? ''
          const pos = it.position ?? ''
          const rank = it.rank ?? ''
          const label = [full, pos, rank].filter(Boolean).join(' — ') || this.$t('kps.officersWork.officerFallback', { n: idx + 1 })
          return {
            key: this.officerKeyFromFields({ full_name: full, position: pos, rank }),
            renderKey: `cmd-${idx}-${full}-${pos}-${rank}`,
            full_name: full,
            position: pos,
            rank: rank,
            label,
          }
        })

        const optionKeySet = new Set((this.officerOptions || []).map((o) => o.key))

        const savedItems = Array.isArray(saved) ? saved : []
        this.rows = savedItems.map((it) => ({
          id: it.id,
          officerKey: (() => {
            const k = this.officerKeyFromFields({
              full_name: it.full_name ?? '',
              position: it.position ?? '',
              rank: it.rank ?? '',
            })
            return optionKeySet.has(k) ? k : ''
          })(),
          position: it.position ?? '',
          rank: it.rank ?? '',
          full_name: it.full_name ?? '',
          goal: it.goal ?? '',
          arrival: this.normalizeToDateTimeLocal(it.arrival),
          departure: this.normalizeToDateTimeLocal(it.departure),
          sign: it.sign ?? '',
        }))

        if (!this.rows.length) this.addRow()
      } catch (e) {
        this.error = e?.message || String(e)
      } finally {
        this.loading = false
      }
    },

    normalizeToDateTimeLocal(value) {
      if (!value) return ''

      // Already datetime-local compatible: YYYY-MM-DDTHH:MM...
      const m = String(value).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/)
      if (m) return `${m[1]}T${m[2]}:${m[3]}`

      // Try parsing ISO with timezone
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return String(value)

      const two = (n) => String(n).padStart(2, '0')
      const yyyy = d.getFullYear()
      const mm = two(d.getMonth() + 1)
      const dd = two(d.getDate())
      const hh = two(d.getHours())
      const mi = two(d.getMinutes())
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
    },

    normalizeToServerDateTime(value) {
      if (!value) return null
      const s = String(value)
      // datetime-local gives YYYY-MM-DDTHH:MM
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return `${s}:00`
      return s
    },

    async save() {
      try {
        this.saving = true
        const rows = (this.rows || []).map((r) => ({
          ...r,
          arrival: this.normalizeToServerDateTime(r.arrival),
          departure: this.normalizeToServerDateTime(r.departure),
        }))
        // Не создаем пустые новые записи (без id)
        const filtered = rows.filter((r) => {
          if (r.id) return true
          const has = [r.position, r.rank, r.full_name, r.goal, r.arrival, r.departure, r.sign]
            .some((v) => String(v || '').trim() !== '')
          return has
        })
        await saveOfficersWork(filtered)
        await this.fetchRows()
        this.showSuccessToast(this.$t('common.saved'))
      } catch (e) {
        console.error('OfficersWork: save failed', e)
        this.showErrorToast(this.$t('common.saveFailed'))
        alert(this.$t('common.saveFailed') + ': ' + (e?.message || e))
      } finally {
        this.saving = false
      }
    },

    addRow() {
      const id = `row-${Date.now()}-${Math.random().toString(16).slice(2)}`
      this.rows.push({
        __id: id,
        officerKey: '',
        position: '',
        rank: '',
        full_name: '',
        goal: '',
        arrival: '',
        departure: '',
        sign: '',
      })
    },

    removeRow(row) {
      const key = this.rowKey(row)
      this.rows = (this.rows || []).filter((r) => this.rowKey(r) !== key)
      if (!this.rows.length) this.addRow()
    },

    applyOfficer(row) {
      const opt = (this.officerOptions || []).find((o) => o.key === row.officerKey)
      if (!opt) return
      row.position = opt.position || row.position
      row.rank = opt.rank || row.rank
      row.full_name = opt.full_name || row.full_name
    },
  },
}
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.page__title { margin: 0; color: #111827; font-size: 24px; font-weight: 700; line-height: 31.2px; }

.section-card {
  position: relative;
  background: #FFFFFF;
  border-radius: 16px;
  padding: 0;
}

.page__footer { display: flex; justify-content: flex-end; margin-top: auto; }

/* Редактирование по паттерну календаря */
.tbl-input { width: 100%; height: 32px; padding: 6px 10px; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 16px; line-height: 20.8px; font-family: 'Times New Roman', Times, serif; color: #111827; background: #fff; }
.tbl-input:focus { outline: 2px solid rgba(47,120,238,.25); outline-offset: 0; }

.tbl-select { width: 100%; height: 32px; padding: 6px 10px; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 14px; line-height: 20.8px; font-family: 'Times New Roman', Times, serif; color: #111827; background: #fff; }
.tbl-select:focus { outline: 2px solid rgba(47,120,238,.25); outline-offset: 0; }

.actions { display: inline-flex; align-items: center; gap: 10px; justify-content: flex-end; width: 100%; }
.actions__btn { width: 30px; height: 30px; padding: 6px; border-radius: 8px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__btn--delete  { background: #D81717; }

/* Toast Notifications (по паттерну VirPerformance/BsfpAttendance) */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  min-width: 220px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}
.toast-success { background: #2A8A1B; }
.toast-error { background: #D81717; }
.toast-content {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}
.toast-slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.toast-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
